import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Snackbar } from '@mui/material'

import { googleActions, apiActions, authActions } from '../redux'
import { LoadingOverlay } from '../components'

import {
  parseGraphMonthLabel,
  sumBucketChargesForGraph,
  mapQboLineTypeToChargeLabelStrict,
  normalizeQboChargeString,
  dedupeQboChargeRows,
  qboChargeRowDedupeKey,
  resolveEnabledN8nTabs,
  pickAvailableN8nTab
} from '../utils'
import { constants } from '../config'
import { useDashboardData, useDashboardGraph } from '../hooks'
import {
  DataDrawer,
  DashboardLoadingStatus,
  DashboardAppBar,
  DashboardChartPaper,
  DataTranspositionBar,
  BillingDetailsDrawer
} from '../components/dashboard'

import {
  formatMonthLabelKey,
  sumAiUnits,
  getBillableMode,
  splitBillableSupportUnits,
  computeDeficit,
  normalizeChargeLabel
} from '../utils/dashboardMath'

import { emptyGraphData, buildEmptyTabStats } from '../utils/dashboardViewConstants'

const { catNames, catOptions, graphFilters, allCharges } = constants

// --- DashboardView ---
const DashboardView = () => {
  const dispatch = useDispatch()
  const filterPopperRef = useRef(null)

  const { user: authUser } = useSelector((state) => state.auth || {})
  const { billingInfo, trackerInfo, ticketsInfo } = useSelector((state) => state.google)
  const {
    organizationTickets,
    vulnerabilityTickets,
    upgradeTickets,
    qboByCustomer,
    loading: isLoading,
    vulnUpgradesLoading,
    qboLoading,
    socrClientSettings,
    socrClientOptions,
    socrProjectAvailability,
    socrN8nTabCapabilities,
    socrCatalogReady,
    socrClientsLoading,
    socrClientsError,
    socrCodeTreeLoading
  } = useSelector((state) => state.api)

  const clientSettings = socrClientSettings || {}
  const clientOptions = socrClientOptions || {}
  const projectAvailabilityFromSocr = socrProjectAvailability || {}
  const n8nTabCapabilities = socrN8nTabCapabilities || {}

  // --- local state ---
  const [graphData, setGraphData] = useState(emptyGraphData)

  const [filtersSelected, setFiltersSelected] = useState({
    [graphFilters.SUPPORT]: true,
    [graphFilters.UPGRADES]: true,
    [graphFilters.VULNERABILITIES]: true
  })

  // Both true ⇒ billable filter shows "all"
  const [billableSelected, setBillableSelected] = useState({
    billable: true,
    nonBillable: true
  })

  const [filterPopper, setFilterPopper] = useState(false)

  const [clientPopperOpen, setClientPopperOpen] = useState(false)
  const [clientAnchorEl, setClientAnchorEl] = useState(null)

  const [projectData, setProjectData] = useState({})
  const [projectGraphLabels, setProjectGraphLabels] = useState([])
  const [projectOptions, setProjectOptions] = useState([])
  const [projectAvailability, setProjectAvailability] = useState({})
  const [clientSelected, setClientSelected] = useState({})
  const [selectedCharges, setSelectedCharges] = useState({})
  // QBO line keys left out of chart totals while their charge type checkbox stays on
  const [excludedChargeRowKeys, setExcludedChargeRowKeys] = useState({})
  const [billingDrawerOpen, setBillingDrawerOpen] = useState(false)
  const [billingChargePopperOpen, setBillingChargePopperOpen] = useState(false)
  const billingChargeAnchorRef = useRef(null)
  const [dataTranspositionOpen, setDataTranspositionOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })
  const prevLoadingRef = useRef(null)
  const dashboardBootstrappedRef = useRef(false)
  /** Previous `chargesPresentInData` keys — used to auto-select only newly available charges when clients/range change */
  const chargesPresentInDataPrevRef = useRef(null)

  // Keyed by tab id; feeds DashboardCounter + tooltips
  const [graphStatistics, setGraphStatistics] = useState({})

  const [activeTab, setActiveTab] = useState(catNames.AI_SP_MONTHLY)
  const [dataSource, setDataSource] = useState('n8n')
  const [dateRangeValue, setDateRangeValue] = useState([0, 12])
  const allChargeOptions = useMemo(() => [...allCharges], [])

  // --- effects ---
  useEffect(() => {
    dispatch(apiActions.fetchSocrClients({ force: false }))
    dispatch(apiActions.fetchSocrCodeTree({ force: false }))
    dispatch(googleActions.fetchSpreadsheet({ force: false }))
    dispatch(apiActions.fetchTickets({ force: false, onFailure: () => { dispatch(authActions.loggedOut()) } }))
    dispatch(apiActions.fetchVulnerabilitiesAndUpgrades())
    dispatch(apiActions.fetchQbo())
  }, [dispatch])

  useEffect(() => {
    if (!allChargeOptions.length) return
    setSelectedCharges((prev) => {
      if (Object.keys(prev || {}).length) return prev
      return allChargeOptions.reduce((initialMap, chargeName) => {
        initialMap[chargeName] = true
        return initialMap
      }, {})
    })
  }, [allChargeOptions])

  useEffect(() => {
    if (prevLoadingRef.current === true && isLoading === false && organizationTickets && Object.keys(organizationTickets).length > 0) {
      setSnackbar({ open: true, message: 'Support issues loaded' })
    }
    prevLoadingRef.current = isLoading
  }, [isLoading, organizationTickets])

  useEffect(() => {
    if (socrClientsError) {
      setSnackbar({ open: true, message: socrClientsError })
    }
  }, [socrClientsError])

  // --- data + graph handlers from hooks ---
  const dashboardData = useDashboardData({
    billingInfo,
    trackerInfo,
    ticketsInfo,
    organizationTickets,
    vulnerabilityTickets,
    upgradeTickets,
    qboByCustomer,
    selectedCharges,
    excludedChargeRowKeys,
    constants,
    graphFilters,
    clientSettings,
    clientOptions,
    projectAvailabilityOverrides: socrCatalogReady ? projectAvailabilityFromSocr : null
  })

  const { handleDataChanges } = useDashboardGraph({
    projectData,
    projectGraphLabels,
    setGraphData,
    setGraphStatistics,
    catNames,
    clientSettings,
    graphFilters,
    selectedCharges,
    excludedChargeRowKeys
  })

  // One-time shell when dashboard data first loads. Charge toggles also refresh dashboardData but must not reset tab/range/clients.
  useEffect(() => {
    if (!socrCatalogReady) {
      dashboardBootstrappedRef.current = false
      return
    }

    if (!dashboardData) {
      dashboardBootstrappedRef.current = false
      return
    }

    const { sampleKey, projectKeys, projectMonthlyData, projectAvailability: availability = {}, monthLabels } = dashboardData
    const stepDifference = monthLabels.length

    const end = Math.max(0, stepDifference)
    const start = Math.max(0, end - 13)
    const nextRange = [start, Math.max(0, end - 1)]

    setProjectAvailability(availability)
    setProjectData(projectMonthlyData)
    setProjectOptions(projectKeys)
    setProjectGraphLabels(monthLabels)

    if (!dashboardBootstrappedRef.current) {
      dashboardBootstrappedRef.current = true

      const defaultSource = 'n8n'
      const initialTabPreferred = catNames.AI_SP_MONTHLY

      setDataSource(defaultSource)
      setDateRangeValue(nextRange)

      const enabledForDefaultSource = projectKeys.filter((name) => availability?.[name]?.[defaultSource])
      const initialClient = enabledForDefaultSource[0] || sampleKey
      setClientSelected(initialClient ? { [initialClient]: true } : {})

      const initialCaps = initialClient ? n8nTabCapabilities[initialClient] : null
      const initialEnabled = resolveEnabledN8nTabs(initialCaps ? [initialCaps] : [])
      const initialTab = pickAvailableN8nTab(initialEnabled, initialTabPreferred) || initialTabPreferred
      setActiveTab(initialTab)

      const sampleLabelSliced = monthLabels.slice(start, end)

      // Seed empty graph shells; handleDataChanges rebuilds AI Dollar/SP for the default n8n tab
      setGraphData({
        [catNames.BILLING]: { datasets: [], labels: [] },
        [catNames.TRACKING]: { datasets: [], labels: [] },
        [catNames.SP_MONTHLY]: { datasets: [], labels: [] },
        [catNames.TICKETS_MONTHLY]: { datasets: [], labels: [] },
        [catNames.AI_BILLING]: { datasets: [], labels: [] },
        [catNames.AI_TRACKING]: { datasets: [], labels: [] },
        [catNames.AI_TICKET_SP]: { datasets: [], labels: [] },
        [catNames.AI_SP_MONTHLY]: {
          datasets: [],
          labels: sampleLabelSliced
        }
      })

      setGraphStatistics((prev) => ({
        ...(prev || {}),
        [initialTab]: buildEmptyTabStats()
      }))
    }
  }, [dashboardData, socrCatalogReady, n8nTabCapabilities])

  // handleDataChanges reads projectData from state; run after setProjectData so the chart sees the new rows.
  useEffect(() => {
    if (!socrCatalogReady || !dashboardBootstrappedRef.current) return
    if (!projectData || typeof projectData !== 'object' || Object.keys(projectData).length === 0) return

    handleDataChanges({
      projectDiff: clientSelected,
      dateRangeDiff: dateRangeValue,
      filterDiff: filtersSelected,
      billableDiff: billableSelected,
      currentTab: activeTab
    }, graphData)
  }, [projectData, projectGraphLabels, excludedChargeRowKeys, selectedCharges, clientSelected, dateRangeValue, filtersSelected, billableSelected, activeTab, socrCatalogReady])

  const sourceAwareProjectOptions = useMemo(() => {
    const sourceKey = dataSource === 'google' ? 'google' : 'n8n'
    return (Array.isArray(projectOptions) ? projectOptions : []).map((projectName) => ({
      name: projectName,
      enabled: Boolean(projectAvailability?.[projectName]?.[sourceKey])
    }))
  }, [projectOptions, projectAvailability, dataSource])

  const sourceEnabledOptions = useMemo(
    () => sourceAwareProjectOptions.filter((client) => client.enabled),
    [sourceAwareProjectOptions]
  )
  const sourceDisabledOptions = useMemo(
    () => sourceAwareProjectOptions.filter((client) => !client.enabled),
    [sourceAwareProjectOptions]
  )

  const selectedClientNames = useMemo(
    () => Object.entries(clientSelected || {}).filter(([, on]) => on).map(([name]) => name),
    [clientSelected]
  )

  const enabledN8nTabs = useMemo(() => {
    const caps = selectedClientNames.map((name) => n8nTabCapabilities[name]).filter(Boolean)
    return resolveEnabledN8nTabs(caps)
  }, [selectedClientNames, n8nTabCapabilities])

  // If current AI tab isn't available for the selected clients, move to one that is.
  useEffect(() => {
    if (dataSource !== 'n8n') return
    if (!selectedClientNames.length) return
    const nextTab = pickAvailableN8nTab(enabledN8nTabs, activeTab)
    if (!nextTab || nextTab === activeTab) return
    setActiveTab(nextTab)
  }, [dataSource, selectedClientNames, enabledN8nTabs, activeTab])

  const projectMonthlyData = dashboardData?.projectMonthlyData

  const resolveChargeCategory = (row) => mapQboLineTypeToChargeLabelStrict(row?.lineType || '') || ''

  const rowPassesBillingDetailsFilter = (row) => {
    const clientName = row?.clientName
    if (!clientName) return false
    const strictLabel = mapQboLineTypeToChargeLabelStrict(row?.lineType || '')
    if (strictLabel == null) return false
    const allowed = clientSettings?.[clientName]?.charges
    const chargeList = Array.isArray(allowed) && allowed.length ? allowed : allCharges
    const allowedSet = new Set(chargeList.map((c) => normalizeQboChargeString(c)))
    return allowedSet.has(normalizeQboChargeString(strictLabel))
  }

  const chargesPresentInData = useMemo(() => {
    const chargeLabelsSeenInRange = new Set()
    if (!projectMonthlyData || typeof projectMonthlyData !== 'object') return chargeLabelsSeenInRange

    const selectedClientNames = Object.entries(clientSelected || {})
      .filter(([, isSelected]) => Boolean(isSelected))
      .map(([projectName]) => projectName)
    if (!selectedClientNames.length) return chargeLabelsSeenInRange

    const [startIdx, endIdx] = Array.isArray(dateRangeValue) ? dateRangeValue : [0, 0]
    const labels = projectGraphLabels || []
    const labelsInRange = labels.slice(startIdx, endIdx + 1)
    const monthKeysInRange = labelsInRange.map((label) => formatMonthLabelKey(label)).filter(Boolean)

    selectedClientNames.forEach((clientName) => {
      const monthBucketsForClient = projectMonthlyData[clientName]
      if (!monthBucketsForClient || typeof monthBucketsForClient !== 'object') return
      monthKeysInRange.forEach((monthKey) => {
        const monthBucket = monthBucketsForClient[monthKey]
        const chargeLines = Array.isArray(monthBucket?.Charges) ? monthBucket.Charges : []
        chargeLines.forEach((line) => {
          const chargeCategory = resolveChargeCategory(line)
          if (chargeCategory) chargeLabelsSeenInRange.add(chargeCategory)
        })
      })
    })
    return chargeLabelsSeenInRange
  }, [projectMonthlyData, clientSelected, dateRangeValue, projectGraphLabels])

  // Depends on which category names exist, not on object identity (monthly data ref churns on every toggle).
  const chargeTypesInDataSignature = useMemo(
    () => [...chargesPresentInData].sort().join('\u0001'),
    [chargesPresentInData]
  )

  useEffect(() => {
    const current = chargesPresentInData
    const prevSet = chargesPresentInDataPrevRef.current
    const newlyPresent = prevSet == null
      ? [...current]
      : [...current].filter((name) => !prevSet.has(name))
    chargesPresentInDataPrevRef.current = new Set(current)

    setSelectedCharges((prev) => {
      const next = { ...prev }
      let changed = false
      allChargeOptions.forEach((chargeName) => {
        if (!current.has(chargeName) && next[chargeName]) {
          next[chargeName] = false
          changed = true
        }
      })
      // Select charges that just became available (e.g. new client brings new QBO categories)
      newlyPresent.forEach((chargeName) => {
        if (!next[chargeName]) {
          next[chargeName] = true
          changed = true
        }
      })
      const anyOn = allChargeOptions.some((name) => next[name] && current.has(name))
      if (!anyOn && current.size > 0) {
        for (const name of current) {
          next[name] = true
        }
        changed = true
      }
      return changed ? next : prev
    })
  }, [chargeTypesInDataSignature, allChargeOptions])

  const qboBillingRows = useMemo(() => {
    if (!projectMonthlyData || typeof projectMonthlyData !== 'object') return []

    const selectedClientNames = Object.entries(clientSelected || {})
      .filter(([, isSelected]) => Boolean(isSelected))
      .map(([projectName]) => projectName)
    if (!selectedClientNames.length) return []

    const [startIdx, endIdx] = Array.isArray(dateRangeValue) ? dateRangeValue : [0, 0]
    const labels = projectGraphLabels || []
    const labelsInRange = labels.slice(startIdx, endIdx + 1)
    const monthKeysInRange = labelsInRange.map((label) => formatMonthLabelKey(label)).filter(Boolean)

    const rows = []
    selectedClientNames.forEach((clientName) => {
      const monthBucketsForClient = projectMonthlyData[clientName]
      if (!monthBucketsForClient || typeof monthBucketsForClient !== 'object') return
      monthKeysInRange.forEach((monthKey) => {
        const monthBucket = monthBucketsForClient[monthKey]
        const chargeLines = Array.isArray(monthBucket?.Charges) ? monthBucket.Charges : []
        chargeLines.forEach((line) => {
          if (rowPassesBillingDetailsFilter(line)) rows.push(line)
        })
      })
    })

    const deduped = dedupeQboChargeRows(rows)

    return deduped.sort((a, b) => String(b.invoiceDate || '').localeCompare(String(a.invoiceDate || '')))
  }, [projectMonthlyData, clientSelected, dateRangeValue, projectGraphLabels])

  const qboBillingTotalsByClient = useMemo(() => {
    return qboBillingRows.reduce((totalsByClient, row) => {
      const clientName = row.clientName
      totalsByClient[clientName] = (totalsByClient[clientName] || 0) + (Number(row.amount) || 0)
      return totalsByClient
    }, {})
  }, [qboBillingRows])

  const billingSelectedClientNames = useMemo(() => {
    return Object.entries(clientSelected || {})
      .filter(([, isOn]) => isOn)
      .map(([name]) => name)
      .sort((a, b) => String(a).localeCompare(String(b)))
  }, [clientSelected])

  const chargesPresentInDataPerClient = useMemo(() => {
    const chargeLabelsByClient = {}
    Object.entries(clientSelected || {}).filter(([, isOn]) => isOn).forEach(([clientName]) => {
      chargeLabelsByClient[clientName] = new Set()
    })
    if (!projectMonthlyData || typeof projectMonthlyData !== 'object') return chargeLabelsByClient

    const [startIdx, endIdx] = Array.isArray(dateRangeValue) ? dateRangeValue : [0, 0]
    const labels = projectGraphLabels || []
    const labelsInRange = labels.slice(startIdx, endIdx + 1)
    const monthKeysInRange = labelsInRange.map((label) => formatMonthLabelKey(label)).filter(Boolean)

    Object.keys(chargeLabelsByClient).forEach((clientName) => {
      const monthBucketsForClient = projectMonthlyData[clientName]
      if (!monthBucketsForClient || typeof monthBucketsForClient !== 'object') return
      monthKeysInRange.forEach((monthKey) => {
        const monthBucket = monthBucketsForClient[monthKey]
        const chargeLines = Array.isArray(monthBucket?.Charges) ? monthBucket.Charges : []
        chargeLines.forEach((line) => {
          const chargeCategory = resolveChargeCategory(line)
          if (chargeCategory) chargeLabelsByClient[clientName].add(chargeCategory)
        })
      })
    })
    return chargeLabelsByClient
  }, [projectMonthlyData, clientSelected, dateRangeValue, projectGraphLabels])

  const qboRowsByClient = useMemo(() => {
    const rowsByClient = {}
    qboBillingRows.forEach((row) => {
      const clientName = row.clientName
      if (!rowsByClient[clientName]) rowsByClient[clientName] = []
      rowsByClient[clientName].push(row)
    })
    return rowsByClient
  }, [qboBillingRows])

  useEffect(() => {
    const valid = new Set((qboBillingRows || []).map((r) => qboChargeRowDedupeKey(r)))
    setExcludedChargeRowKeys((ex) => {
      if (!ex || typeof ex !== 'object' || Object.keys(ex).length === 0) return ex
      const next = {}
      let changed = false
      Object.keys(ex).forEach((k) => {
        if (valid.has(k)) next[k] = true
        else changed = true
      })
      return changed ? next : ex
    })
  }, [qboBillingRows])

  useEffect(() => {
    setSelectedCharges((prev) => {
      const next = { ...prev }
      let changed = false
      chargesPresentInData.forEach((catName) => {
        const normCat = normalizeChargeLabel(catName)
        const rowsInCat = qboBillingRows.filter(
          (r) => normalizeChargeLabel(r.chargeCategory || '') === normCat
        )
        if (!rowsInCat.length) return
        const anyIncluded = rowsInCat.some((r) => {
          if (!next[catName]) return false
          return !excludedChargeRowKeys[qboChargeRowDedupeKey(r)]
        })
        if (next[catName] && !anyIncluded) {
          next[catName] = false
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [excludedChargeRowKeys, qboBillingRows, chargesPresentInData])

  const resolveCategoryKeyForRow = useCallback((row) => {
    const norm = normalizeChargeLabel(row?.chargeCategory || '')
    if (!norm) return ''
    for (const name of chargesPresentInData) {
      if (normalizeChargeLabel(name) === norm) return name
    }
    const exact = allChargeOptions.find((n) => normalizeChargeLabel(n) === norm)
    return exact || row.chargeCategory || ''
  }, [chargesPresentInData, allChargeOptions])

  const handleChargeCategoryToggle = useCallback((chargeName, checked) => {
    setSelectedCharges((prev) => {
      const next = { ...prev, [chargeName]: checked }
      const hasAny = Object.entries(next).some(([k, v]) => v && chargesPresentInData.has(k))
      return hasAny ? next : { ...next, [chargeName]: true }
    })
    const norm = normalizeChargeLabel(chargeName)
    setExcludedChargeRowKeys((ex) => {
      const n = { ...(ex || {}) }
      qboBillingRows.forEach((row) => {
        if (normalizeChargeLabel(row.chargeCategory || '') !== norm) return
        delete n[qboChargeRowDedupeKey(row)]
      })
      return n
    })
  }, [chargesPresentInData, qboBillingRows])

  const handleChargeRowInGraphToggle = useCallback((row, includeInGraph) => {
    const key = qboChargeRowDedupeKey(row)
    const catKey = resolveCategoryKeyForRow(row)
    if (includeInGraph && catKey && chargesPresentInData.has(catKey)) {
      setSelectedCharges((prev) => {
        if (prev[catKey]) return prev
        return { ...prev, [catKey]: true }
      })
    }
    setExcludedChargeRowKeys((ex) => {
      const next = { ...(ex || {}) }
      if (includeInGraph) delete next[key]
      else next[key] = true
      return next
    })
  }, [chargesPresentInData, resolveCategoryKeyForRow])

  const handleSelectAllVisibleCharges = useCallback((rows, includeAll) => {
    if (includeAll && rows.length) {
      const catsToOn = new Set()
      rows.forEach((row) => {
        const catKey = resolveCategoryKeyForRow(row)
        if (catKey && chargesPresentInData.has(catKey)) catsToOn.add(catKey)
      })
      if (catsToOn.size) {
        setSelectedCharges((prev) => {
          const next = { ...prev }
          let changed = false
          catsToOn.forEach((c) => {
            if (!next[c]) {
              next[c] = true
              changed = true
            }
          })
          return changed ? next : prev
        })
      }
    }
    setExcludedChargeRowKeys((ex) => {
      const next = { ...(ex || {}) }
      rows.forEach((row) => {
        const catKey = resolveCategoryKeyForRow(row)
        if (!catKey || !chargesPresentInData.has(catKey)) return
        if (!includeAll && !selectedCharges[catKey]) return
        const k = qboChargeRowDedupeKey(row)
        if (includeAll) delete next[k]
        else next[k] = true
      })
      return next
    })
  }, [chargesPresentInData, selectedCharges, resolveCategoryKeyForRow])

  const selectedChargeCategoryCount = useMemo(() => {
    return allChargeOptions.filter((name) => selectedCharges?.[name] && chargesPresentInData.has(name)).length
  }, [allChargeOptions, selectedCharges, chargesPresentInData])

  const chargeCategorySubtitle = (chargeName) => {
    if (!chargesPresentInData.has(chargeName)) {
      return 'Not in QBO for this date range'
    }
    const withCharge = billingSelectedClientNames.filter((c) => chargesPresentInDataPerClient[c]?.has(chargeName))
    if (withCharge.length === billingSelectedClientNames.length) return 'All selected clients'
    if (withCharge.length === 0) return 'Not in QBO for this date range'
    return `Available for: ${withCharge.join(', ')}`
  }

  useEffect(() => {
    const sourceKey = dataSource === 'google' ? 'google' : 'n8n'
    setClientSelected((prev) => {
      const next = Object.entries(prev || {}).reduce((obj, [projectName, isSelected]) => {
        if (!isSelected) return obj
        if (projectAvailability?.[projectName]?.[sourceKey]) obj[projectName] = true
        return obj
      }, {})
      if (Object.keys(next).length) return next
      const fallback = (Array.isArray(projectOptions) ? projectOptions : []).find((name) => projectAvailability?.[name]?.[sourceKey])
      return fallback ? { [fallback]: true } : {}
    })
  }, [dataSource, projectAvailability, projectOptions])

  // --- event handlers ---
  const handleClientChange = (e) => {
    const sourceKey = dataSource === 'google' ? 'google' : 'n8n'
    if (!projectAvailability?.[e.target.name]?.[sourceKey]) return
    const tempClientsSelected = { ...clientSelected, [e.target.name]: e.target.checked }
    setClientSelected(tempClientsSelected)

    handleDataChanges({
      projectDiff: tempClientsSelected,
      dateRangeDiff: dateRangeValue,
      filterDiff: filtersSelected,
      billableDiff: billableSelected,
      currentTab: activeTab
    }, graphData)
  }

  const handleTabChange = (e, newTab) => {
    if (typeof newTab !== 'string' || !newTab) return

    const isAiTab = (newTab === catNames.AI_BILLING || newTab === catNames.AI_SP_MONTHLY || newTab === catNames.AI_TRACKING || newTab === catNames.AI_TICKET_SP)
    const isGoogleTab = (newTab === catNames.SP_MONTHLY || newTab === catNames.BILLING || newTab === catNames.TRACKING || newTab === catNames.TICKETS_MONTHLY)

    if (isAiTab && dataSource === 'n8n' && !enabledN8nTabs?.[newTab]) return

    setActiveTab(newTab)

    handleDataChanges({
      projectDiff: clientSelected,
      dateRangeDiff: dateRangeValue,
      filterDiff: filtersSelected,
      billableDiff: billableSelected,
      currentTab: newTab
    }, graphData)

    if (isAiTab && dataSource !== 'n8n') setDataSource('n8n')
    if (isGoogleTab && dataSource !== 'google') setDataSource('google')
  }

  const handleLabelFormat = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return ''

    const startDateStr = projectGraphLabels?.[0]
    if (!startDateStr) return ''

    const baseDate = parseGraphMonthLabel(startDateStr)
    if (!baseDate || Number.isNaN(baseDate.getTime())) return ''

    const dateObj = new Date(baseDate.getFullYear(), baseDate.getMonth() + value, 1)

    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    })
  }

  const handleSliderChange = (e, newValue) => {
    if (!Array.isArray(newValue) || newValue.length !== 2) return

    handleDataChanges({
      projectDiff: clientSelected,
      dateRangeDiff: newValue,
      filterDiff: filtersSelected,
      billableDiff: billableSelected,
      currentTab: activeTab
    }, graphData)

    setDateRangeValue(newValue)
  }

  const handleSourceChange = (sourceType) => {
    const googleToN8n = {
      [catNames.SP_MONTHLY]: catNames.AI_SP_MONTHLY,
      [catNames.BILLING]: catNames.AI_BILLING,
      [catNames.TRACKING]: catNames.AI_TRACKING,
      [catNames.TICKETS_MONTHLY]: catNames.AI_TICKET_SP
    }
    const n8nToGoogle = {
      [catNames.AI_SP_MONTHLY]: catNames.SP_MONTHLY,
      [catNames.AI_BILLING]: catNames.BILLING,
      [catNames.AI_TRACKING]: catNames.TRACKING,
      [catNames.AI_TICKET_SP]: catNames.TICKETS_MONTHLY
    }

    if (sourceType === 'n8n') {
      const mapped = googleToN8n[activeTab]
      const n8nSelected = selectedClientNames.filter((name) => projectAvailability?.[name]?.n8n)
      const fallbackClient = (Array.isArray(projectOptions) ? projectOptions : []).find((name) => projectAvailability?.[name]?.n8n)
      const namesForCaps = n8nSelected.length ? n8nSelected : (fallbackClient ? [fallbackClient] : [])
      const caps = namesForCaps.map((name) => n8nTabCapabilities[name]).filter(Boolean)
      const enabled = resolveEnabledN8nTabs(caps)
      const nextTab = pickAvailableN8nTab(enabled, mapped) || mapped || catNames.AI_SP_MONTHLY
      setActiveTab(nextTab)
      handleDataChanges({
        projectDiff: clientSelected,
        dateRangeDiff: dateRangeValue,
        filterDiff: filtersSelected,
        billableDiff: billableSelected,
        currentTab: nextTab
      }, graphData)
    } else if (sourceType === 'google' && n8nToGoogle[activeTab]) {
      handleTabChange(null, n8nToGoogle[activeTab])
    }

    setDataSource(sourceType)
  }

  const handleFilterChange = (e) => {
    const tempFiltersSelected = { ...filtersSelected, [e.target.name]: e.target.checked }
    setFiltersSelected(tempFiltersSelected)

    handleDataChanges({
      projectDiff: clientSelected,
      dateRangeDiff: dateRangeValue,
      filterDiff: tempFiltersSelected,
      billableDiff: billableSelected,
      currentTab: activeTab
    }, graphData)
  }

  const handleBillableChange = (e) => {
    const name = e?.target?.name

    if (name === 'billableMode') {
      const mode = e?.target?.value
      const temp = {
        billable: mode === 'billable',
        nonBillable: mode === 'nonBillable'
      }

      setBillableSelected(temp)

      handleDataChanges({
        projectDiff: clientSelected,
        dateRangeDiff: dateRangeValue,
        filterDiff: filtersSelected,
        billableDiff: temp,
        currentTab: activeTab
      }, graphData)

      return
    }

    const temp = { ...billableSelected, [e.target.name]: e.target.checked }

    if (!temp.billable && !temp.nonBillable) {
      temp.billable = true
      temp.nonBillable = true
    }

    setBillableSelected(temp)

    handleDataChanges({
      projectDiff: clientSelected,
      dateRangeDiff: dateRangeValue,
      filterDiff: filtersSelected,
      billableDiff: temp,
      currentTab: activeTab
    }, graphData)
  }

  const handleClearAll = () => {
    setGraphData(emptyGraphData)
    setGraphStatistics({})
    setClientSelected({})
  }

  const handleClearIssueFilters = () => {
    const cleared = {
      [graphFilters.SUPPORT]: false,
      [graphFilters.UPGRADES]: false,
      [graphFilters.VULNERABILITIES]: false
    }
    setFiltersSelected(cleared)
    handleDataChanges({
      projectDiff: clientSelected,
      dateRangeDiff: dateRangeValue,
      filterDiff: cleared,
      billableDiff: billableSelected,
      currentTab: activeTab
    }, graphData)
    setFilterPopper(false)
  }

  const handleClearCharges = () => {
    setSelectedCharges((prev) => {
      const next = { ...prev }
      chargesPresentInData.forEach((name) => {
        next[name] = true
      })
      return next
    })
    setExcludedChargeRowKeys({})
    setBillingChargePopperOpen(false)
  }

  const closeClientPopper = () => {
    setClientPopperOpen(false)
    setClientAnchorEl(null)
  }

  const toggleClientPopper = (e) => {
    if (clientPopperOpen) {
      closeClientPopper()
      return
    }

    setClientAnchorEl(e?.currentTarget || null)
    setClientPopperOpen(true)
  }

  const tooltipOptions = useMemo(() => {
    const effectiveDollarsPerSP = graphStatistics?.[activeTab]?.EffectiveDollarsPerSP

    return {
      enabled: true,
      backgroundColor: '#FFF',
      titleColor: '#000',
      bodyColor: '#000',
      bodySpacing: 10,
      titleFont: { size: 16, weight: 'bold' },
      bodyfont: { size: 14 },
      padding: 12,
      displayColors: true,
      callbacks: {
        label: (tooltipItem) => {
          if (!tooltipItem || typeof tooltipItem !== 'object') return ''

          const { dataset, dataIndex, label: dataLabel } = tooltipItem
          const projectName = dataset?.label
          const rawValue = dataset?.data?.[dataIndex]

          const monthKey = formatMonthLabelKey(dataLabel)
          const monthData = projectData?.[projectName]?.[monthKey]
          if (!monthData) return `${dataLabel}, value: ${rawValue}`

          if (activeTab === catNames.AI_BILLING) {
            const qbo = sumBucketChargesForGraph(monthData, selectedCharges, excludedChargeRowKeys)
            return `${projectName} | QBO billing: $${qbo.toLocaleString()}`
          }

          const dollars = (
            activeTab === catNames.AI_SP_MONTHLY ||
            activeTab === catNames.AI_TRACKING ||
            activeTab === catNames.AI_TICKET_SP
          )
            ? sumBucketChargesForGraph(monthData, selectedCharges, excludedChargeRowKeys)
            : Number(monthData.Dollars || 0)

          const items = [
            ...(Array.isArray(monthData.Tickets) ? monthData.Tickets : []),
            ...(Array.isArray(monthData.Upgrades) ? monthData.Upgrades : []),
            ...(Array.isArray(monthData.Vulnerabilities) ? monthData.Vulnerabilities : [])
          ]

          const hasAnySupportTypeFilter = Object.values(filtersSelected).some(Boolean)
          const typeFiltered = !hasAnySupportTypeFilter
            ? []
            : items.filter((t) => {
              if (!t || typeof t !== 'object') return false
              const type = t.supportType
              if (!Object.prototype.hasOwnProperty.call(filtersSelected, type)) return false
              return Boolean(filtersSelected[type])
            })

          const billableMode = getBillableMode(billableSelected)

          const billableFiltered = typeFiltered.filter((t) => {
            if (!t || typeof t !== 'object') return false
            if (billableMode === 'all') return true
            if (billableMode === 'billable') return t.isBillable === true
            if (billableMode === 'nonbillable') return t.isBillable !== true
            return true
          })

          const units = sumAiUnits(billableFiltered, graphFilters)
          const dollarsPerUnit = dollars / (units > 0 ? units : 1)

          const split = splitBillableSupportUnits(typeFiltered, graphFilters)
          const nonBillableSP = split.nonBillable

          if (billableMode === 'nonbillable') {
            // Deficit line matches chart/counter: effective $/SP × non-billable SP this month
            const deficit = (typeof effectiveDollarsPerSP === 'number' && effectiveDollarsPerSP >= 0)
              ? effectiveDollarsPerSP * nonBillableSP
              : computeDeficit(dollars, split.billable, nonBillableSP)
            const deficitPerSP = nonBillableSP > 0 ? deficit / nonBillableSP : (effectiveDollarsPerSP ?? 0)
            return `${projectName} | Dollars: $${dollars.toLocaleString()} | Non-billable SP: ${nonBillableSP.toLocaleString()} | Deficit: $${deficit.toFixed(2)} | Deficit $/SP: $${deficitPerSP.toFixed(2)}`
          }

          return `${projectName} | Dollars: $${dollars.toLocaleString()} | SP: ${units.toLocaleString()} | $/Unit: $${dollarsPerUnit.toFixed(2)}`
        }
      }
    }
  }, [projectData, filtersSelected, billableSelected, activeTab, graphStatistics, selectedCharges, excludedChargeRowKeys])

  const conjoinedOptions = useMemo(() => {
    return {
      ...catOptions,
      [catNames.SP_MONTHLY]: {
        ...catOptions[catNames.SP_MONTHLY],
        graphInfo: {
          ...catOptions[catNames.SP_MONTHLY].graphInfo,
          tooltipOptions
        }
      },
      [catNames.AI_BILLING]: {
        ...catOptions[catNames.AI_BILLING],
        graphInfo: {
          ...catOptions[catNames.AI_BILLING].graphInfo,
          tooltipOptions
        }
      },
      [catNames.AI_SP_MONTHLY]: {
        ...catOptions[catNames.AI_SP_MONTHLY],
        graphInfo: {
          ...catOptions[catNames.AI_SP_MONTHLY].graphInfo,
          tooltipOptions
        }
      },
      [catNames.AI_TICKET_SP]: {
        graphInfo: {
          ...catOptions[catNames.AI_TICKET_SP].graphInfo,
          tooltipOptions
        }
      }
    }
  }, [tooltipOptions])

  // --- render ---
  return (
    <>
      <LoadingOverlay props={{ open: Boolean(isLoading || socrClientsLoading || (!socrCatalogReady && !socrClientsError)) }} />

      <DashboardLoadingStatus
        isLoading={isLoading || socrClientsLoading}
        vulnUpgradesLoading={vulnUpgradesLoading}
        qboLoading={qboLoading}
        codeTreeLoading={socrCodeTreeLoading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ '& .MuiSnackbar-content': { borderRadius: 1 } }}
      />

      <DashboardAppBar
        props={{ authUser, activeTab, dataSource, enabledN8nTabs }}
        controls={{ dispatch, handleTabChange, handleSourceChange }}
      />

      <DashboardChartPaper
        props={{
          dateRangeValue,
          projectGraphLabels,
          activeTab,
          graphStatistics,
          billableSelected,
          graphData,
          conjoinedOptions,
          projectOptions
        }}
        controls={{
          handleSliderChange,
          handleLabelFormat
        }}
      />

      <DataTranspositionBar
        props={{
          dataTranspositionOpen,
          clientSelected,
          clientPopperOpen,
          clientAnchorEl,
          dataSource,
          filterPopper,
          vulnUpgradesLoading,
          filtersSelected,
          billableSelected,
          billingChargePopperOpen,
          selectedChargeCategoryCount,
          allChargeOptions,
          chargesPresentInData,
          selectedCharges,
          chargeCategorySubtitle,
          sourceEnabledOptions,
          sourceDisabledOptions,
          filterPopperRef,
          billingChargeAnchorRef,
          graphFilters,
          qboBillingRows,
          excludedChargeRowKeys,
          clientSettings,
          n8nTabCapabilities
        }}
        controls={{
          setDataTranspositionOpen,
          toggleClientPopper,
          closeClientPopper,
          handleClearAll,
          handleClientChange,
          setFilterPopper,
          handleFilterChange,
          handleBillableChange,
          setBillingChargePopperOpen,
          handleClearIssueFilters,
          handleClearCharges,
          handleChargeCategoryToggle,
          handleChargeRowInGraphToggle,
          handleSelectAllVisibleCharges
        }}
      />

      <BillingDetailsDrawer
        props={{
          dataTranspositionOpen,
          open: billingDrawerOpen,
          qboBillingRows,
          billingSelectedClientNames,
          qboRowsByClient,
          qboBillingTotalsByClient,
          clientSettings
        }}
        controls={{ onOpenChange: setBillingDrawerOpen }}
      />

      <DataDrawer
        props={{
          dataTranspositionOpen,
          activeTab,
          catOptions,
          catNames,
          graphStatistics,
          graphData,
          projectOptions,
          clientSelected,
          projectData,
          filtersSelected,
          billableSelected,
          graphFilters,
          clientSettings,
          dateRangeValue,
          projectGraphLabels,
          selectedCharges,
          excludedChargeRowKeys
        }}
        controls={{
          handleClearAll: () => {
            const allSelected = {}
            projectOptions.forEach((name) => {
              allSelected[name] = true
            })
            setClientSelected(allSelected)
            handleDataChanges({
              projectDiff: allSelected,
              dateRangeDiff: dateRangeValue,
              filterDiff: filtersSelected,
              billableDiff: billableSelected,
              currentTab: activeTab
            }, graphData)
          },
          handleClientChange
        }}
      />
    </>
  )
}

export { DashboardView }
